import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import {
  RestrictionName,
  SortDirection,
  UserRestrictionEntry,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import {
  BUCKET_FREE,
  BUCKET_OVERAGE,
  UsageWindow,
  quotaUsage,
  userQuotaByBucket,
} from "./use-org-ai-credit";
import { listOrgUserRestrictions } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import DisplayHeading from "components/shared/display-heading";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { NO_DISPLAY_DATA } from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface OrgAIUsageSectionProps {
  roleId: string;
  regionCode: string;
  usageWindow: UsageWindow;
  ready: boolean;
}

const PAGE_SIZE = 25;

const displayUserName = (entry: UserRestrictionEntry): string => {
  const { first_name, last_name, email, username } = entry.metadata;
  const fullName = [first_name, last_name].filter(Boolean).join(" ").trim();
  return fullName || email || username || entry.userId;
};

const OrgAIUsageSection = ({
  roleId,
  regionCode,
  usageWindow,
  ready,
}: OrgAIUsageSectionProps) => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(true);
  const [entries, setEntries] = useState<UserRestrictionEntry[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);

  const loadUsers = useCallback(
    async (pageNumber: number) => {
      setIsLoading(true);
      try {
        const response = await listOrgUserRestrictions(
          {
            organizationId: toUUID(roleId),
            restrictionName: RestrictionName.AI_CREDIT_USAGE,
            sortDirection: SortDirection.DESC,
            pageNumber,
            pageSize: PAGE_SIZE,
            fromTime: usageWindow.from,
            toTime: usageWindow.to,
          },
          callOptionsHeaders(regionCode, roleId)
        );
        setEntries(response.entries);
        setTotalItems(response.totalItems);
      } catch (err) {
        showError(err as GrpcError);
      } finally {
        setIsLoading(false);
      }
    },
    [roleId, regionCode, showError, usageWindow]
  );

  useEffect(() => {
    if (!ready) return;
    setPage(0);
    loadUsers(1);
  }, [ready, loadUsers]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    loadUsers(newPage + 1);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <DisplayHeading>Usage</DisplayHeading>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="org-ai-user-usage-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "35%" }}>User</TableCell>
                <TableCell style={{ width: "15%" }}>Seat type</TableCell>
                <TableCell align="right">Total grant</TableCell>
                <TableCell align="right">Total used</TableCell>
                <TableCell align="right">Free used</TableCell>
                <TableCell align="right">Overage used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <b>No AI credit usage found for this organisation.</b>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const byBucket = userQuotaByBucket(entry.restriction);
                  const freeUsed = quotaUsage(byBucket[BUCKET_FREE]);
                  const overageUsed = quotaUsage(byBucket[BUCKET_OVERAGE]);
                  const totalGrant = Object.values(byBucket).reduce(
                    (sum, quota) => sum + quota.limit,
                    0
                  );
                  return (
                    <TableRow key={entry.userId} data-testid="ai-usage-row">
                      <TableCell>
                        <Box>{displayUserName(entry)}</Box>
                        {entry.metadata.email && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.metadata.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.metadata.seat_type || NO_DISPLAY_DATA}
                      </TableCell>
                      <TableCell align="right">{totalGrant}</TableCell>
                      <TableCell align="right">
                        {freeUsed + overageUsed}
                      </TableCell>
                      <TableCell align="right">{freeUsed}</TableCell>
                      <TableCell align="right">{overageUsed}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[PAGE_SIZE]}
                  count={totalItems}
                  rowsPerPage={PAGE_SIZE}
                  page={page}
                  onPageChange={handleChangePage}
                  ActionsComponent={RPCTablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default OrgAIUsageSection;
